import type { FunctionComponent, ReactNode, Context } from 'react';
import { createContext, useContext, useEffect, useReducer } from 'react';

import { buildHashParam } from '../utils/buildHash';
import { DEFAULT_LANGUAGE_CODE } from '../utils/language';

import { Fetcher } from './Fetcher';

const basePath = location.pathname.substring(0, location.pathname.lastIndexOf('/'));

interface Translations {
  game: Record<string, string>;
}

interface TranslationState {
  isFetching: boolean;
  hasErrors: boolean;
  translations: Record<string, string>;
}

interface TranslationProviderProps {
  children: ReactNode;
  currentLanguage: string;
}

enum TranslationAction {
  SUCCESS,
  ERROR,
}

const initialState: TranslationState = {
  isFetching: true,
  hasErrors: false,
  translations: {},
};

// ToDo: This is done due to multiple instances of the same context being created in the storybook
const TranslationContext: Context<TranslationState> = ((): Context<TranslationState> => {
  const cache: { translationContext?: Context<TranslationState> } = global ? (global as never) : {};
  cache.translationContext = cache.translationContext || createContext<TranslationState>(initialState);
  return cache.translationContext;
})();

const translationReducer = (
  state: TranslationState,
  action: {
    type: TranslationAction;
    payload: Record<string, string>;
  }
): TranslationState => ({
  isFetching: false,
  hasErrors: action.type === TranslationAction.SUCCESS,
  translations: action.payload,
});

export const TranslationProvider: FunctionComponent<TranslationProviderProps> = ({ children, currentLanguage }) => {
  const [state, dispatch] = useReducer(translationReducer, initialState);

  useEffect(() => {
    if (!currentLanguage) {
      return;
    }

    const fetchTranslation = (language: string): void => {
      Fetcher.fetch<Translations>(`${basePath}/assets/translations/ui/${language}.json`, buildHashParam)
        .then((result: Translations) => result.game)
        .then((translations) => {
          dispatch({ type: TranslationAction.SUCCESS, payload: translations });
        })
        .catch((error) => {
          if (language !== DEFAULT_LANGUAGE_CODE) {
            console.error(
              `Failed to fetch game translation ${language}.json, falling back to ${DEFAULT_LANGUAGE_CODE}.json`,
              error
            );
            fetchTranslation(DEFAULT_LANGUAGE_CODE);
          } else {
            console.error(`Failed to fetch game translation en.json`, error);
            dispatch({ type: TranslationAction.ERROR, payload: initialState.translations });
          }
        });
    };

    fetchTranslation(currentLanguage);
  }, [currentLanguage]);

  return <TranslationContext.Provider value={state}>{children}</TranslationContext.Provider>;
};

export const useTranslation = (): TranslationState => {
  const { translations, isFetching, hasErrors } = useContext(TranslationContext);

  return {
    hasErrors,
    isFetching,
    translations,
  };
};
